// ContactInfo.jsx
import { useTranslation } from "react-i18next";
import {
  Wrap,
  Inner,
  Head,
  BrandRow,
  BrandDot,
  Title,
  Grid,
  Block,
  TopBar,
  TopBarIcon,
  TopBarTitle,
  List,
  Item,
  Key,
  Val,
  Actions,
  CallBtn,
  MapBtn,
  Note,
} from "./ContactInfoStyles";

// icons (aurii) — rămân la fel
const IconClock = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M12 7v5l3 2"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconMap = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M12 22s7-6.1 7-12A7 7 0 1 0 5 10c0 5.9 7 12 7 12Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle
      cx="12"
      cy="10"
      r="2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);
const IconPhone = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.9 4.2 2 2 0 0 1 4.9 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L9 9c1.3 2.3 3.1 4.1 5.4 5.4l.6-.9a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.6.6A2 2 0 0 1 22 16.9Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

export default function ContactInfo({
  brand,
  accent = "#d4af37",
  phones = ["+40 268 456 156", "+40 721 456 156", "+40 766 456 156"], // ✅
  address = "Bulevardul Mihai Săulescu nr.111, Predeal, România",
  mapsUrl = "https://www.google.com/maps?q=Mihai+Săulescu+111,+Predeal",
  note,
  schedule,
}) {
  const { t } = useTranslation();

  // brand din i18n cu fallback la prop / default
  const brandLabel =
    brand ?? t("brandFull", { defaultValue: "La Fontanella Restaurant" });

  // zile + etichete din i18n; dacă primești schedule ca prop, îl folosim
  const defaultSchedule = [
    { k: t("contact.days.mon"), v: "08:00 – 22:00" },
    { k: t("contact.days.tue"), v: "08:00 – 22:00" },
    { k: t("contact.days.wed"), v: "08:00 – 22:00" },
    { k: t("contact.days.thu"), v: "08:00 – 22:00" },
    { k: t("contact.days.fri"), v: "08:00 – 22:00" },
    { k: t("contact.days.sat"), v: "08:00 – 22:00" },
    { k: t("contact.days.sun"), v: "08:00 – 22:00" },
  ];
  const rows = schedule ?? defaultSchedule;

  const toTel = (n) => n.replace(/[^\d+]/g, ""); // curățăm spații/paranteze
  const phoneList = (phones && phones.length ? phones : [phone]).filter(
    Boolean,
  );

  return (
    <Wrap
      aria-label={t("contact.aria", { defaultValue: "Contact information" })}
      style={{ "--accent": accent }}
    >
      <Inner itemScope itemType="https://schema.org/Restaurant">
        <Head>
          <BrandRow>
            <BrandDot />
            <Title itemProp="name">{brandLabel}</Title>
            <BrandDot />
          </BrandRow>
        </Head>

        <Grid>
          {/* Program */}
          <Block>
            <TopBar>
              <TopBarIcon as={IconClock} />
              <TopBarTitle>
                {t("contact.program", { defaultValue: "Program" })}
              </TopBarTitle>
            </TopBar>
            <List>
              {rows.map((row) => (
                <Item key={row.k}>
                  <Key>{row.k}</Key>
                  <Val>{row.v}</Val>
                </Item>
              ))}
            </List>
          </Block>

          {/* Adresă */}
          <Block
            itemProp="address"
            itemScope
            itemType="https://schema.org/PostalAddress"
          >
            <TopBar>
              <TopBarIcon as={IconMap} />
              <TopBarTitle>
                {t("contact.address.title", { defaultValue: "Address" })}
              </TopBarTitle>
            </TopBar>
            <List>
              <Item>
                <Key>
                  {t("contact.address.location", { defaultValue: "Location" })}
                </Key>
                <Val itemProp="streetAddress">{address}</Val>
              </Item>
            </List>
            <Actions $center>
              <MapBtn href={mapsUrl} target="_blank" rel="noopener">
                {t("contact.address.openMaps", {
                  defaultValue: "Open in Maps",
                })}
              </MapBtn>
            </Actions>
          </Block>

          {/* Rezervări */}
          {/* Rezervări */}
          <Block>
            <TopBar>
              <TopBarIcon as={IconPhone} />
              <TopBarTitle>
                {t("contact.booking.title", { defaultValue: "Reservations" })}
              </TopBarTitle>
            </TopBar>

            <List>
              {phoneList.map((num, i) => (
                <Item key={`${num}-${i}`}>
                  {/* eticheta "Telefon" doar pe primul rând */}
                  <Key>
                    {i === 0
                      ? t("contact.booking.phone", { defaultValue: "Phone" })
                      : ""}
                  </Key>

                  <Val>
                    <CallBtn
                      itemProp="telephone"
                      href={`tel:${toTel(num)}`}
                      aria-label={`Sună la ${num}`}
                    >
                      {num}
                    </CallBtn>
                  </Val>
                </Item>
              ))}
            </List>

            {(note ?? t("contact.booking.note", { defaultValue: "" })) && (
              <Note>{note ?? t("contact.booking.note")}</Note>
            )}
          </Block>
        </Grid>
      </Inner>
    </Wrap>
  );
}
